/**
 * 将 Step2 comp_draw.attachment 或 Step4 meta.drawings 规范化为附图列表。
 */

/** 修正「图1是图1 是……」等重复图号（兼容历史数据） */
export const sanitizeFigureDescription = (text, figureNo = '') => {
  if (!text) return ''
  let value = String(text).trim().replace(/图\s+(\d+)/g, '图$1')
  if (!figureNo) return value

  const index = figureNo.replace(/^图/, '')
  const escaped = figureNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  value = value.replace(new RegExp(`^${escaped}是(?:图${index}\\s*)+是?\\s*`), `${figureNo}是`)
  value = value.replace(new RegExp(`^${escaped}是图${index}\\s*`), `${figureNo}是`)
  value = value.replace(new RegExp(`^${escaped}\\s+是`), `${figureNo}是`)

  if (value.startsWith(figureNo)) return value
  if (value.startsWith('是')) return `${figureNo}${value}`
  if (new RegExp(`^图${index}`).test(value)) {
    return value.replace(new RegExp(`^图${index}\\s*是\\s*`), `${figureNo}是`)
  }
  return value
}

/** 去掉展示时已单独列出 figureNo 的句首重复（如「图9是…」→「本发明…」） */
export const stripDuplicateFigureNoPrefix = (text, figureNo = '') => {
  if (!text) return ''
  const value = String(text).trim()
  if (!figureNo) return value

  const escaped = figureNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (new RegExp(`^${escaped}是`).test(value)) {
    return value.replace(new RegExp(`^${escaped}是`), '').trim()
  }
  if (new RegExp(`^${escaped}[\\s，,、]`).test(value)) {
    return value.replace(new RegExp(`^${escaped}[\\s，,、]+`), '').trim()
  }
  return value
}

/** 附图卡片副标题：优先短 title；无 title 时再 fallback 去重后的说明句 */
export const figureCaptionText = (fig) => {
  if (!fig || typeof fig !== 'object') return ''
  const title = String(fig.title || '').trim()
  if (title) return title
  const formal = String(fig.suggestedDrawingDescription || fig.description || '').trim()
  if (formal) {
    return stripDuplicateFigureNoPrefix(formal, fig.figureNo)
  }
  return ''
}

export const normalizeDrawings = (source) => {
  const list = Array.isArray(source) ? source : []
  return list
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null
      const figureIndex = Number(item.figureIndex) || index + 1
      const figureNo = String(item.figureNo || `图${figureIndex}`).trim()
      const description = sanitizeFigureDescription(item.description, figureNo)
      const suggestedDrawingDescription = sanitizeFigureDescription(
        item.suggestedDrawingDescription,
        figureNo,
      )
      return {
        id: String(item.id ?? item.filePath ?? figureIndex),
        fileName: String(item.fileName || '').trim(),
        filePath: String(item.filePath || '').trim(),
        fileUrl: String(item.fileUrl || '').trim(),
        figureIndex,
        figureNo,
        figureType: String(item.figureType || '').trim(),
        title: String(item.title || '').trim(),
        description,
        suggestedDrawingDescription,
        components: Array.isArray(item.components) ? item.components : [],
        technicalPoints: Array.isArray(item.technicalPoints) ? item.technicalPoints : [],
      }
    })
    .filter(Boolean)
}

export const mergeAttachmentWithAnalysis = (attachment, docResult) => {
  const attachments = normalizeDrawings(attachment)
  let figures = []
  if (docResult) {
    try {
      const parsed = typeof docResult === 'string' ? JSON.parse(docResult) : docResult
      figures = normalizeDrawings(parsed?.figures)
    } catch {
      figures = []
    }
  }
  if (!attachments.length) {
    return figures
  }
  return attachments.map((item, index) => {
    const match = figures.find(
      (fig) => fig.figureIndex === item.figureIndex || fig.id === item.id,
    ) || figures[index]
    if (!match) return item
    return {
      ...item,
      ...match,
      fileUrl: item.fileUrl || match.fileUrl,
    }
  })
}

export const resolveStep4Drawings = ({ step4Drawings, step2Attachment, step2DocResult, metaDrawings } = {}) => {
  const fromApi = normalizeDrawings(step4Drawings)
  if (fromApi.length) return fromApi

  const fromMeta = normalizeDrawings(metaDrawings)
  if (fromMeta.length) return fromMeta

  return mergeAttachmentWithAnalysis(step2Attachment, step2DocResult)
}

export const drawingFigureLabels = (drawings = []) =>
  drawings.map((item) => item.figureNo).join('、')
